import { useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { ArrowUp } from 'lucide-react'
import { AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useBackgroundContrast } from '@/hooks/useBackgroundContrast'
import { useChatBot } from '@/contexts/ChatBotContext'

export function ScrollToTop() {
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const [showButton, setShowButton] = useState(false)
  const buttonRef = useRef<HTMLDivElement>(null)
  const background = useBackgroundContrast(buttonRef)
  const isDarkOrPrimary = background === 'dark' || background === 'primary'
  const { isOpen: isChatOpen } = useChatBot()

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  // Show/hide back-to-top button
  useEffect(() => {
    const onScroll = () => setShowButton(window.scrollY > 400)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <AnimatePresence>
      {showButton && !isChatOpen && (
        <div
          ref={buttonRef}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={cn(
            'fixed bottom-24 right-6 z-[600] flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            isDarkOrPrimary
              ? 'bg-white hover:bg-gray-100 text-gray-900'
              : 'bg-primary-600 hover:bg-primary-700 text-white'
          )}
          role="button"
          aria-label={t('aria.backToTop')}
        >
          <ArrowUp className="h-5 w-5" />
        </div>
      )}
    </AnimatePresence>
  )
}
